import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtInt, fmtNum, fmtQty } from '../../utils/api';
import * as XLSX from 'xlsx';

export default function ProfitabilityPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'accounting');
  const [data, setData] = useState(null);
  const [cellcomOrders, setCellcomOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [expandedCard, setExpandedCard] = useState(null);
  const [quarterData, setQuarterData] = useState(null);
  const [view, setView] = useState('main');  // 'main' | 'simulator'

  // ──── Simulator state ────
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [scenario, setScenario] = useState(null); // { scenario, orderNumbers, overrides, orders, totals }
  const [simSelected, setSimSelected] = useState(new Set());  // order_numbers checked
  const [simOverrides, setSimOverrides] = useState({});       // {item_id: priceAfterCredit}
  const [simResult, setSimResult] = useState(null);
  const [simName, setSimName] = useState('');
  const [simSaving, setSimSaving] = useState(false);
  const [allCellcomOrders, setAllCellcomOrders] = useState([]);  // for picking

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [profRes, coRes, qRes] = await Promise.all([
        api.get('/profitability'),
        api.get('/profitability/cellcom-orders'),
        api.get('/quarter'),
      ]);
      setData(profRes.data);
      setCellcomOrders(coRes.data.orders || []);
      setQuarterData(qRes.data);
    } catch {} finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ──── Simulator helpers ────
  const fetchScenarios = useCallback(async () => {
    try { const r = await api.get('/profitability/simulator'); setScenarios(r.data || []); } catch {}
  }, []);
  const fetchAllCellcomOrders = useCallback(async () => {
    try { const r = await api.get('/warehouse/cellcom-orders'); setAllCellcomOrders(r.data || []); } catch {}
  }, []);
  const loadScenario = useCallback(async (id) => {
    try {
      const r = await api.get(`/profitability/simulator/${id}`);
      setScenario(r.data);
      const orderNums = new Set((r.data.orders || []).map(o => String(o.orderNumber)));
      setSimSelected(orderNums);
      setSimResult({ orders: r.data.orders, totals: r.data.totals });
      setActiveScenarioId(id);
      setSimName(r.data.scenario?.name || '');
    } catch {}
  }, []);
  // Refetch results from backend (after any clone/uncloan/price-edit)
  const refreshScenario = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/profitability/simulator/${id}`);
      setSimResult({ orders: r.data.orders, totals: r.data.totals });
      const orderNums = new Set((r.data.orders || []).map(o => String(o.orderNumber)));
      setSimSelected(orderNums);
    } catch {}
  }, []);

  useEffect(() => {
    if (view === 'simulator') {
      fetchScenarios();
      fetchAllCellcomOrders();
    }
  }, [view, fetchScenarios, fetchAllCellcomOrders]);

  const toggleOrder = (num) => setExpanded(prev => {
    const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s;
  });

  const updateItem = async (orderNumber, sku, field, value) => {
    const body = { orderNumber, sku };
    if (field === 'mExpenses') body.mExpenses = value;
    if (field === 'customerPrice') body.customerPrice = value;
    try {
      await api.put('/profitability/cellcom-item', body);
      fetchData(true);
    } catch {}
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;
  if (!data) return <div className="empty-state">{t('common.noData')}</div>;

  const s = data.summary || {};

  // Headline = sum of the rows actually displayed in the formula below.
  // Render the components the backend returns; derive the total from them so the
  // headline can never diverge from the visible rows (income + credits - cogs - expenses - rebate).
  const displayedProfit = (s.invoiceIncome || 0) + (s.creditIncome || 0) - (s.cogs || 0) - (s.totalExpenses || 0) - (s.customerRebate || 0);

  // Aggregate profitability from cellcom orders
  const ordersRevenue = cellcomOrders.reduce((sum, o) => sum + (o.totalRevenue || 0), 0);
  const ordersProfit = cellcomOrders.reduce((sum, o) => sum + (o.totalProfit || 0), 0);
  const ordersMargin = ordersRevenue > 0 ? (ordersProfit / ordersRevenue * 100) : 0;

  const he = t('dir') === 'rtl' || document.documentElement.dir === 'rtl';
  const targetsReached = s.dealTargetsReached || [];

  return (
    <div>
      {/* Tab nav: main / simulator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        <button className={`btn btn-sm ${view === 'main' ? 'btn-gold' : 'btn-outline'}`} style={{ borderRadius: '8px 8px 0 0', border: 0, fontWeight: 700 }} onClick={() => setView('main')}>
          {he ? 'רווחיות' : 'Profitability'}
        </button>
        <button className={`btn btn-sm ${view === 'simulator' ? 'btn-gold' : 'btn-outline'}`} style={{ borderRadius: '8px 8px 0 0', border: 0, fontWeight: 700 }} onClick={() => setView('simulator')}>
          🧪 {he ? 'סימולטור' : 'Simulator'}
        </button>
        <button className={`btn btn-sm ${view === 'dealCheck' ? 'btn-gold' : 'btn-outline'}`} style={{ borderRadius: '8px 8px 0 0', border: 0, fontWeight: 700 }} onClick={() => setView('dealCheck')}>
          🤝 {he ? 'בדיקת עסקה' : 'Deal Check'}
        </button>
      </div>

      {view === 'simulator' ? (
        <SimulatorView
          scenarios={scenarios}
          activeScenarioId={activeScenarioId}
          allOrders={allCellcomOrders}
          selected={simSelected}
          overrides={simOverrides}
          result={simResult}
          name={simName}
          saving={simSaving}
          scenario={scenario}
          he={he}
          onNameChange={setSimName}
          onCreate={async () => {
            if (!simName.trim()) { alert(he ? 'הזן שם תרחיש' : 'Enter scenario name'); return; }
            setSimSaving(true);
            try {
              const r = await api.post('/profitability/simulator', { name: simName.trim() });
              await fetchScenarios();
              await loadScenario(r.data.id);
            } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
            finally { setSimSaving(false); }
          }}
          onLoad={loadScenario}
          onDelete={async (id) => {
            if (!window.confirm(he ? 'למחוק את התרחיש?' : 'Delete scenario?')) return;
            try { await api.delete(`/profitability/simulator/${id}`); if (id === activeScenarioId) { setActiveScenarioId(null); setScenario(null); setSimResult(null); setSimSelected(new Set()); setSimOverrides({}); setSimName(''); } await fetchScenarios(); } catch {}
          }}
          onToggleOrder={async (orderNumber) => {
            if (!activeScenarioId) return;
            try {
              if (simSelected.has(orderNumber)) {
                await api.delete(`/profitability/simulator/${activeScenarioId}/orders/${orderNumber}`);
              } else {
                await api.post(`/profitability/simulator/${activeScenarioId}/orders/${orderNumber}`);
              }
              await refreshScenario(activeScenarioId);
              await fetchScenarios();
            } catch (e) {
              alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message));
            }
          }}
          onCostChange={async (itemId, value) => {
            const v = parseFloat(value);
            if (!Number.isFinite(v) || v < 0) return;
            // Optimistic local update for snappy UI
            setSimResult(prev => {
              if (!prev) return prev;
              const orders = prev.orders.map(o => ({
                ...o,
                items: o.items.map(it => it.id === itemId ? { ...it, simulatedCost: v } : it),
              }));
              return { ...prev, orders };
            });
            try {
              await api.put(`/profitability/simulator/items/${itemId}`, { simulatedCost: v });
              await refreshScenario(activeScenarioId);
            } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
          }}
          onSaveName={async () => {
            if (!activeScenarioId) return;
            try {
              await api.put(`/profitability/simulator/${activeScenarioId}`, { name: simName });
              await fetchScenarios();
            } catch {}
          }}
          onRefreshClone={async () => {
            if (!activeScenarioId) return;
            if (!window.confirm(he ? 'לרענן את ההזמנות מנתוני cellcom העדכניים? כל מחיר אחרי קרדיט שערכת — יאופס למחיר המקורי החדש.' : 'Refresh cloned orders from current cellcom data? Any "price after credit" you edited will be reset to the new selling_price.')) return;
            try {
              await api.post(`/profitability/simulator/${activeScenarioId}/refresh-clone`);
              await refreshScenario(activeScenarioId);
            } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
          }}
          onApplyDefaults={async () => {
            if (!activeScenarioId) return;
            try {
              const r = await api.post(`/profitability/simulator/${activeScenarioId}/apply-defaults`);
              await refreshScenario(activeScenarioId);
              alert((he ? 'הוחל ל-' : 'Applied to ') + (r.data.applied || 0) + (he ? ' פריטים' : ' items'));
            } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
          }}
          onBulkCost={async (sku, value) => {
            if (!activeScenarioId) return;
            try {
              await api.put(`/profitability/simulator/${activeScenarioId}/items-by-sku`, { sku, simulatedCost: value });
              await refreshScenario(activeScenarioId);
            } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
          }}
        />
      ) : view === 'dealCheck' ? (
        <DealCheckView he={he} />
      ) : (<>
      {/* Target-reached banner */}
      {targetsReached.length > 0 && (
        <div style={{ padding: 12, marginBottom: 16, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, color: '#166534' }}>
          <strong>✓ {he ? 'דילים שהגיעו ליעד' : 'Deals Reached Target'}: </strong>
          {targetsReached.map(d => d.name).join(', ')}
        </div>
      )}

      {/* Summary Cards — clickable */}
      <div className="summary-cards">
        {[
          { key: 'stock', color: 'gold', label: t('profitability.stockValueLabel'), value: fmtInt(s.stockValue), sub: `${fmtQty(s.stockUnits || 0)} units` },
          { key: 'credits', color: 'info', label: t('profitability.credits'), value: fmtInt(s.creditIncome) },
          { key: 'dealEarned', color: 'gold', style: { borderLeft: '4px solid #f59e0b' }, label: he ? 'זיכוי דילים שהרווחנו' : 'Deal Credit Earned', value: fmtInt(s.dealCreditEarned || 0), sub: `${he ? 'מתוך' : 'of'} ${fmtInt(s.dealCreditTotal || 0)} · ${he ? 'התקבל' : 'rec\u2019d'}: ${fmtInt(s.dealCreditReceived || 0)}` },
          { key: 'rebate', color: '', style: { borderLeftColor: '#ff9800' }, label: t('profitability.customerRebate'), value: fmtInt(s.customerRebate), valueStyle: { color: '#e65100' }, sub: `${he ? 'מתוך' : 'of'} ${fmtInt((s.customerRebate || 0) * 100)}` },
          { key: 'orders', color: ordersProfit >= 0 ? 'success' : 'danger', style: { borderLeft: '4px solid #9c27b0' }, label: t('profitability.ordersProfit'), value: fmtInt(ordersProfit), sub: `${fmtNum(ordersMargin)}% · ${cellcomOrders.length} orders` },
          { key: 'total', color: displayedProfit >= 0 ? 'success' : 'danger', label: t('profitability.totalProfit'), value: fmtInt(displayedProfit), sub: `${s.invoiceIncome > 0 ? fmtNum(s.margin) : '0'}%` },
        ].map(card => (
          <div key={card.key} className={`summary-card clickable ${card.color}`} style={{ ...(card.style || {}), outline: expandedCard === card.key ? '2px solid var(--gold)' : 'none', position: 'relative' }}
            onClick={() => setExpandedCard(expandedCard === card.key ? null : card.key)}>
            <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>
              ⓘ
            </div>
            <div className="label">{card.label}</div>
            <div className="value" style={card.valueStyle}>{card.value}</div>
            {card.sub && <div className="sub">{card.sub}</div>}
          </div>
        ))}
      </div>

      {/* Quarterly cards row */}
      {cellcomOrders.length > 0 && (() => {
        const qData = {};
        for (const o of cellcomOrders) {
          const q = o.quarter || 'Unknown';
          if (!qData[q]) qData[q] = { revenue: 0, cogs: 0, profit: 0, qty: 0, count: 0, rebate: 0 };
          qData[q].revenue += o.totalRevenue || 0;
          qData[q].cogs += o.items?.reduce((s2, i) => s2 + i.purchaseLoaded * i.quantity, 0) || 0;
          qData[q].profit += o.totalProfit || 0;
          qData[q].qty += o.totalQty || 0;
          qData[q].count++;
          qData[q].rebate += (o.items?.reduce((s2, i) => s2 + i.customerPrice * i.quantity, 0) || 0) * 0.01;
        }
        // Allocate every headline component (income/credits/cogs/expenses/rebate) across
        // quarters by revenue share, so the per-quarter totals sum exactly to the headline
        // (displayedProfit). With a single quarter the share is 1 → card == headline.
        const qKeys = Object.keys(qData);
        const totalRev = Object.values(qData).reduce((s2,d) => s2+d.revenue, 0);
        for (const q of qKeys) {
          const r = qKeys.length === 1 ? 1 : (totalRev > 0 ? qData[q].revenue / totalRev : 0);
          qData[q].income = (s.invoiceIncome || 0) * r;
          qData[q].credits = (s.creditIncome || 0) * r;
          qData[q].allocCogs = (s.cogs || 0) * r;
          qData[q].expenses = (s.totalExpenses || 0) * r;
          qData[q].allocRebate = (s.customerRebate || 0) * r;
          qData[q].totalProfit = qData[q].income + qData[q].credits - qData[q].allocCogs - qData[q].expenses - qData[q].allocRebate;
          qData[q].margin = qData[q].income > 0 ? (qData[q].totalProfit / qData[q].income * 100) : 0;
        }
        return (
          <div className="summary-cards" style={{ marginBottom: 8 }}>
            {Object.entries(qData).sort(([a],[b]) => a.localeCompare(b)).map(([q, d]) => (
              <div key={q} className={`summary-card clickable ${d.totalProfit >= 0 ? 'success' : 'danger'}`}
                style={{ outline: expandedCard === 'q-'+q ? '2px solid var(--gold)' : 'none', position: 'relative' }}
                onClick={() => setExpandedCard(expandedCard === 'q-'+q ? null : 'q-'+q)}>
                <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>
                  ⓘ
                </div>
                <div className="label">{q}</div>
                <div className="value">{fmtInt(d.totalProfit)}</div>
                <div className="sub">{fmtNum(d.margin)}% · {d.count} orders</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Expanded card detail */}
      {expandedCard && (
        <div className="card mb-16" style={{ padding: 16 }}>
          {expandedCard === 'stock' && (
            <div>
              <h4 style={{ marginBottom: 8, color: 'var(--text-dim)' }}>{t('profitability.stockValueLabel')} — Breakdown</h4>
              <div className="table-wrap"><table><thead><tr><th>{t('common.sku')}</th><th>{t('common.product')}</th><th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Unit Cost</th><th className="text-end">{t('common.total')}</th></tr></thead>
              <tbody>{(data?.stockBreakdown || []).map((item, i) => (
                <tr key={i}><td>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td><td>{item.description}</td><td className="text-end">{fmtQty(item.qty)}</td><td className="text-end">{fmt(item.unitCost)}</td><td className="text-end fw-bold">{fmt(item.value)}</td></tr>
              ))}</tbody></table></div>
            </div>
          )}
          {expandedCard === 'credits' && (
            <div>
              <h4 style={{ marginBottom: 8, color: 'var(--text-dim)' }}>{t('profitability.credits')} — Breakdown</h4>
              <div className="table-wrap"><table><thead><tr><th>Order</th><th>{t('common.description')}</th><th className="text-end">Amount</th></tr></thead>
              <tbody>{(data?.creditBreakdown || []).map((item, i) => (
                <tr key={i}><td className="text-gold">{item.orderNo}</td><td>{item.description}</td><td className="text-end fw-bold text-success">{fmt(item.amount)}</td></tr>
              ))}</tbody></table></div>
            </div>
          )}
          {expandedCard === 'rebate' && (
            <div>
              <h4 style={{ marginBottom: 8, color: 'var(--text-dim)' }}>{t('profitability.customerRebate')} — Breakdown</h4>
              <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>1% of total sell-out ({fmtInt((s.customerRebate || 0) * 100)})</p>
              <div className="table-wrap"><table><thead><tr><th>Order</th><th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Invoice Total</th><th className="text-end">Rebate 1%</th></tr></thead>
              <tbody>{cellcomOrders.map(o => (
                <tr key={o.orderNumber}><td className="text-gold">#{o.orderNumber}</td><td className="text-end">{fmtQty(o.totalQty)}</td><td className="text-end">{fmt(o.items?.reduce((s2, i) => s2 + i.customerPrice * i.quantity, 0) || 0)}</td><td className="text-end text-danger">{fmt((o.items?.reduce((s2, i) => s2 + i.customerPrice * i.quantity, 0) || 0) * 0.01)}</td></tr>
              ))}</tbody></table></div>
            </div>
          )}
          {expandedCard === 'orders' && (
            <div>
              <h4 style={{ marginBottom: 8, color: 'var(--text-dim)' }}>{t('profitability.ordersProfit')} — By Order</h4>
              <div className="table-wrap"><table><thead><tr><th>Order</th><th>{t('common.date')}</th><th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Revenue</th><th className="text-end">{t('profitability.profit')}</th><th className="text-end">Margin</th></tr></thead>
              <tbody>{cellcomOrders.map(o => (
                <tr key={o.orderNumber}><td className="text-gold">#{o.orderNumber}</td><td>{new Date(o.createdAt).toLocaleDateString()}</td><td className="text-end">{fmtQty(o.totalQty)}</td><td className="text-end">{fmtInt(o.totalRevenue)}</td><td className={`text-end fw-bold ${o.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtInt(o.totalProfit)}</td><td className="text-end">{fmtNum(o.margin)}%</td></tr>
              ))}</tbody></table></div>
            </div>
          )}
          {expandedCard === 'total' && (
            <div>
              <h4 style={{ marginBottom: 8, color: 'var(--text-dim)' }}>{t('profitability.totalProfit')} — Formula</h4>
              <div className="table-wrap"><table><tbody>
                <tr><td>{t('profitability.invoiceIncome')}</td><td className="text-end fw-bold">{fmtInt(s.invoiceIncome)}</td></tr>
                <tr><td>+ {t('profitability.creditIncome')}</td><td className="text-end fw-bold">{fmtInt(s.creditIncome)}</td></tr>
                <tr><td>- {t('profitability.cogs')}</td><td className="text-end fw-bold">{fmtInt(s.cogs)}</td></tr>
                <tr><td>- {t('profitability.expenses')}</td><td className="text-end fw-bold">{fmtInt(s.totalExpenses)}</td></tr>
                <tr><td>- {t('profitability.customerRebate')}</td><td className="text-end fw-bold">{fmtInt(s.customerRebate)}</td></tr>
                <tr style={{ borderTop: '2px solid var(--border)' }}><td className="fw-bold">= {t('profitability.totalProfit')}</td><td className={`text-end fw-bold ${displayedProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtInt(displayedProfit)}</td></tr>
              </tbody></table></div>
            </div>
          )}
        </div>
      )}

      {/* Formula */}
      <div className="formula mb-16">
        <div><span className="lbl">{t('profitability.invoiceIncome')}</span><span className="val">{fmtInt(s.invoiceIncome)}</span></div>
        <span className="op">+</span>
        <div><span className="lbl">{t('profitability.creditIncome')}</span><span className="val">{fmtInt(s.creditIncome)}</span></div>
        <span className="op">-</span>
        <div><span className="lbl">{t('profitability.cogs')}</span><span className="val">{fmtInt(s.cogs)}</span></div>
        <span className="op">-</span>
        <div><span className="lbl">{t('profitability.expenses')}</span><span className="val">{fmtInt(s.totalExpenses)}</span></div>
        <span className="op">-</span>
        <div><span className="lbl">{t('profitability.rebatePercent')}</span><span className="val">{fmtInt(s.customerRebate)}</span></div>
        <span className="op">=</span>
        <div><span className="lbl">{t('profitability.profit')}</span><span className={`val ${displayedProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtInt(displayedProfit)}</span></div>
      </div>

      {/* Quarterly expand detail */}
      {cellcomOrders.length > 0 && (() => {
        const byQ = {};
        for (const o of cellcomOrders) {
          const q = o.quarter || 'Unknown';
          if (!byQ[q]) byQ[q] = { revenue: 0, cogs: 0, profit: 0, qty: 0, orders: [], credits: 0, expenses: 0, rebate: 0 };
          byQ[q].revenue += o.totalRevenue || 0;
          byQ[q].cogs += o.items?.reduce((s2, i) => s2 + i.purchaseLoaded * i.quantity, 0) || 0;
          byQ[q].profit += o.totalProfit || 0;
          byQ[q].qty += o.totalQty || 0;
          byQ[q].orders.push(o);
          byQ[q].rebate += (o.items?.reduce((s2, i) => s2 + i.customerPrice * i.quantity, 0) || 0) * 0.01;
        }
        const totalCredits = s.creditIncome || 0;
        const totalExpenses = s.totalExpenses || 0;
        const qKeys = Object.keys(byQ);
        const totalRev = Object.values(byQ).reduce((s2,d) => s2+d.revenue, 0);
        for (const q of qKeys) {
          const r = qKeys.length === 1 ? 1 : (totalRev > 0 ? byQ[q].revenue / totalRev : 0);
          byQ[q].credits = totalCredits * r;
          byQ[q].expenses = totalExpenses * r;
        }

        // Get quarter purchase data
        const SUPPORT_RATE = 0.045;
        const qPurchase = {};
        if (quarterData?.quarters) {
          for (const qq of quarterData.quarters) {
            qPurchase[qq.quarter] = { productTotal: qq.productTotal, compTotal: qq.compTotal, net: qq.net, support: (qq.productTotal || 0) * SUPPORT_RATE };
          }
        }

        return Object.entries(byQ).map(([q, d]) => {
          if (expandedCard !== 'q-'+q) return null;
          const qProfit = d.revenue + d.credits - d.cogs - d.expenses - d.rebate;
          const purchase = qPurchase[q] || {};
          return (
            <div key={q} className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                {/* Sales side */}
                <div>
                  <h4 style={{ marginBottom: 12, color: 'var(--text-dim)' }}>Sales</h4>
                  <div className="table-wrap">
                    <table><tbody>
                      <tr><td>Revenue (Shager gets)</td><td className="text-end fw-bold">{fmtInt(d.revenue)}</td></tr>
                      <tr><td>+ {t('profitability.credits')}</td><td className="text-end fw-bold text-success">{fmtInt(d.credits)}</td></tr>
                      <tr><td>- COGS</td><td className="text-end fw-bold">{fmtInt(d.cogs)}</td></tr>
                      <tr><td>- {t('profitability.expenses')}</td><td className="text-end fw-bold">{fmtInt(d.expenses)}</td></tr>
                      <tr><td>- Rebate 1%</td><td className="text-end fw-bold" style={{ color: '#e65100' }}>{fmtInt(d.rebate)}</td></tr>
                      <tr style={{ borderTop: '2px solid var(--border)' }}><td className="fw-bold">= {t('profitability.profit')}</td><td className={`text-end fw-bold ${qProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtInt(qProfit)}</td></tr>
                    </tbody></table>
                  </div>
                </div>
                {/* Purchase side */}
                <div>
                  <h4 style={{ marginBottom: 12, color: 'var(--text-dim)' }}>Purchase (SI)</h4>
                  <div className="table-wrap">
                    <table><tbody>
                      <tr><td>SI Purchases</td><td className="text-end fw-bold">{fmtInt(purchase.productTotal)}</td></tr>
                      <tr><td>Compensations</td><td className="text-end fw-bold text-danger">{fmtInt(purchase.compTotal)}</td></tr>
                      <tr><td>Net</td><td className="text-end fw-bold">{fmtInt(purchase.net)}</td></tr>
                      <tr style={{ borderTop: '1px solid var(--border)' }}><td>Support 2%</td><td className="text-end fw-bold text-success">{fmtInt(purchase.support)}</td></tr>
                    </tbody></table>
                  </div>
                </div>
              </div>

              <h4 style={{ marginTop: 16, marginBottom: 8, color: 'var(--text-dim)' }}>Cellcom Orders</h4>
              <div className="table-wrap">
                <table><thead><tr><th>Order</th><th>{t('common.date')}</th><th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Revenue</th><th className="text-end">{t('profitability.profit')}</th><th className="text-end">Margin</th></tr></thead>
                <tbody>{d.orders.map(o => (
                  <tr key={o.orderNumber}><td className="text-gold">#{o.orderNumber}</td><td>{new Date(o.createdAt).toLocaleDateString()}</td><td className="text-end">{fmtQty(o.totalQty)}</td><td className="text-end">{fmtInt(o.totalRevenue)}</td><td className={`text-end fw-bold ${o.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmtInt(o.totalProfit)}</td><td className="text-end">{fmtNum(o.margin)}%</td></tr>
                ))}</tbody></table>
              </div>
            </div>
          );
        });
      })()}

      {/* Cellcom Orders Profitability */}
      <div style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16, color: 'var(--text-dim)' }}>
          {t('profitability.cellcomOrders')}
        </h2>

        {cellcomOrders.length === 0 ? (
          <div className="empty-state">{t('common.noData')}</div>
        ) : (() => {
          // Group by quarter
          const byQuarter = {};
          for (const o of cellcomOrders) {
            const q = o.quarter || 'Unknown';
            if (!byQuarter[q]) byQuarter[q] = [];
            byQuarter[q].push(o);
          }
          return Object.entries(byQuarter).map(([quarter, orders]) => {
            const qProfit = orders.reduce((s, o) => s + o.totalProfit, 0);
            const qRevenue = orders.reduce((s, o) => s + o.totalRevenue, 0);
            const qMargin = qRevenue > 0 ? (qProfit / qRevenue * 100) : 0;
            return (
              <div key={quarter} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '2px solid var(--border)', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{quarter}</span>
                  <span className="text-success fw-bold">{t('profitability.profit')}: {fmtInt(qProfit)} ({fmtNum(qMargin)}%)</span>
                </div>
                {orders.map(o => {
          const isOpen = expanded.has(o.orderNumber);
          return (
            <div key={o.orderNumber} className="order-group" style={{ marginBottom: 8 }}>
              <div className="order-group-header" onClick={() => toggleOrder(o.orderNumber)} style={{ cursor: 'pointer' }}>
                <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                <span className="order-no">#{o.orderNumber}</span>
                <span className="text-muted">{new Date(o.createdAt).toLocaleDateString()}</span>
                <div style={{ flex: 1 }} />
                <span className="text-muted">{fmtQty(o.totalQty)} units</span>
                <span className={`fw-bold ${o.totalProfit >= 0 ? 'text-success' : 'text-danger'}`} style={{ marginInlineStart: 16 }}>
                  {t('profitability.profit')}: {fmtInt(o.totalProfit)}
                </span>
                <span className="text-muted" style={{ marginInlineStart: 8 }}>({fmtNum(o.margin)}%)</span>
              </div>
              {isOpen && (
                <div className="order-group-body">
                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => {
                      const rows = o.items.map(i => ({
                        'Order': o.orderNumber,
                        'SKU': i.sku,
                        'Barcode': i.barcode || '',
                        'Product': i.description,
                        'QTY': i.quantity,
                        'Base Price (Cost)': i.basePrice || '',
                        'Purchase Loaded': i.purchaseLoaded || '',
                        'C Price': i.cPrice || '',
                        'M Price': i.mPrice || '',
                        'Customer Price': i.customerPrice || '',
                        'M Profit': i.customerPrice > 0 && i.mPrice > 0 ? Math.round((i.customerPrice - i.mPrice) * i.quantity * 100) / 100 : 0,
                        'C Profit': i.mPrice > 0 && i.cPrice > 0 ? Math.round((i.mPrice - i.cPrice) * i.quantity * 100) / 100 : 0,
                        'Shager Profit': i.profit || 0,
                      }));
                      const totalMProfit = rows.reduce((s, r) => s + (r['M Profit'] || 0), 0);
                      const totalCProfit = rows.reduce((s, r) => s + (r['C Profit'] || 0), 0);
                      rows.push({ 'Order': '', 'SKU': '', 'Product': 'TOTAL', 'QTY': o.totalQty, 'Base Price (Cost)': '', 'Purchase Loaded': '', 'C Price': '', 'M Price': '', 'Customer Price': '', 'M Profit': totalMProfit, 'C Profit': totalCProfit, 'Shager Profit': o.totalProfit });
                      const ws = XLSX.utils.json_to_sheet(rows);
                      ws['!cols'] = [{wch:10},{wch:10},{wch:30},{wch:8},{wch:14},{wch:16},{wch:12},{wch:12},{wch:16},{wch:12},{wch:12},{wch:12}];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Profitability');
                      XLSX.writeFile(wb, `profitability_${o.orderNumber}.xlsx`);
                    }}>Export Excel</button>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-dim)' }}>{t('profitability.mExpenses')}</span>
                    {canEdit ? (
                      <input className="inline-edit" type="number" step="0.01" style={{ width: 100, textAlign: 'right' }}
                        key={`me-${o.orderNumber}-${o.mExpenses}`}
                        defaultValue={o.mExpenses || ''}
                        placeholder="0"
                        onBlur={e => {
                          const v = parseFloat(e.target.value) || 0;
                          if (v !== (o.mExpenses || 0)) updateItem(o.orderNumber, null, 'mExpenses', v);
                        }}
                      />
                    ) : <span className="fw-bold">{fmt(o.mExpenses || 0)}</span>}
                  </div>
                  <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{t('common.sku')}</th>
                      <th>{t('common.product')}</th>
                      <th className="text-end">QTY</th>
                      <th className="text-end">{t('profitability.basePrice')}</th>
                      <th className="text-end">{t('profitability.purchaseLoaded')}</th>
                      <th className="text-end" style={{ background: '#e3f2fd' }}>{t('profitability.cPrice')}</th>
                      <th className="text-end" style={{ background: '#fff3e0' }}>{t('profitability.mPrice')}</th>
                      <th className="text-end" style={{ background: '#e8f5e9' }}>{t('profitability.customerPrice')}</th>
                      <th className="text-end" style={{ background: '#fff3e0', fontSize: 10 }}>M PROFIT</th>
                      <th className="text-end" style={{ background: '#e3f2fd', fontSize: 10 }}>C PROFIT</th>
                      <th className="text-end fw-bold" style={{ background: '#e8f5e9', fontSize: 10 }}>SHAGER PROFIT</th>
                    </tr></thead>
                    <tbody>
                      {o.items.map((item, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10, fontFamily: 'Arial,sans-serif' }}>{item.barcode}</div>}</td>
                          <td>{item.description}</td>
                          <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                          <td className="text-end">{item.basePrice > 0 ? fmt(item.basePrice) : '—'}</td>
                          <td className="text-end">{item.purchaseLoaded > 0 ? fmt(item.purchaseLoaded) : '—'}</td>
                          <td className="text-end" style={{ background: '#f5f9ff' }}>{item.cPrice > 0 ? fmt(item.cPrice) : '—'}</td>
                          <td className="text-end" style={{ background: '#fffbf5' }}>{item.mPrice > 0 ? fmt(item.mPrice) : '—'}</td>
                          <td className="text-end" style={{ background: '#f5fff5' }}>
                            {canEdit ? (
                              <input className="inline-edit" type="number" step="0.01" style={{ width: 80, textAlign: 'right' }}
                                key={`cp-${o.orderNumber}-${item.sku}-${item.customerPrice}`}
                                defaultValue={item.customerPrice || ''}
                                placeholder="0"
                                onBlur={e => {
                                  const v = parseFloat(e.target.value) || 0;
                                  if (v !== item.customerPrice) updateItem(o.orderNumber, item.sku, 'customerPrice', v);
                                }}
                              />
                            ) : (item.customerPrice > 0 ? fmt(item.customerPrice) : '—')}
                          </td>
                          <td className="text-end" style={{ background: '#fffbf5', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                            {item.customerPrice > 0 && item.mPrice > 0 ? fmt((item.customerPrice - item.mPrice) * item.quantity) : '—'}
                          </td>
                          <td className="text-end" style={{ background: '#f5f9ff', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                            {item.mPrice > 0 && item.cPrice > 0 ? fmt((item.mPrice - item.cPrice) * item.quantity) : '—'}
                          </td>
                          <td className={`text-end fw-bold ${item.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {fmt(item.profit)}
                          </td>
                        </tr>
                      ))}
                      {(() => {
                        const totalMProfit = o.items.reduce((s, i) => s + (i.customerPrice > 0 && i.mPrice > 0 ? (i.customerPrice - i.mPrice) * i.quantity : 0), 0);
                        const totalCProfit = o.items.reduce((s, i) => s + (i.mPrice > 0 && i.cPrice > 0 ? (i.mPrice - i.cPrice) * i.quantity : 0), 0);
                        return (
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td colSpan={2}>{t('common.total')}</td>
                            <td className="text-end">{fmtQty(o.totalQty)}</td>
                            <td colSpan={5}></td>
                            <td className="text-end" style={{ background: '#fffbf5' }}>{fmt(totalMProfit)}</td>
                            <td className="text-end" style={{ background: '#f5f9ff' }}>{fmt(totalCProfit)}</td>
                            <td className={`text-end ${o.totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>{fmt(o.totalProfit)}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
              </div>
            );
          });
        })()}
      </div>
      </>)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Simulator sub-view: scenario list + order picker + items editor
// ─────────────────────────────────────────────────────────────────
function SimulatorView({
  scenarios, activeScenarioId, allOrders, selected, overrides, result, name, saving, scenario, he,
  onNameChange, onCreate, onLoad, onDelete, onToggleOrder, onCostChange, onSaveName, onRefreshClone, onApplyDefaults, onBulkCost,
}) {
  const fmtUsd = (v) => v == null || isNaN(v) ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [sortField, setSortField] = useState('description');
  const [sortDir, setSortDir] = useState('asc');
  const [itemSearch, setItemSearch] = useState('');
  const onSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } };
  const arrow = (f) => sortField === f ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const filterItems = (items) => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      String(it.sku || '').toLowerCase().includes(q) ||
      String(it.description || '').toLowerCase().includes(q) ||
      String(it.sourceOrderNo || '').toLowerCase().includes(q)
    );
  };
  const sortItems = (items) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = a[sortField], vb = b[sortField];
      if (typeof va === 'string' || typeof vb === 'string') return String(va || '').localeCompare(String(vb || ''), undefined, { sensitivity: 'base' }) * dir;
      return ((va || 0) - (vb || 0)) * dir;
    });
  };
  return (
    <div>
      {/* Scenario list + new */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, marginInlineEnd: 'auto' }}>{he ? 'תרחישי סימולטור' : 'Simulator Scenarios'}</h3>
          <input className="input input-sm" placeholder={he ? 'שם תרחיש חדש' : 'New scenario name'} value={name} onChange={e => onNameChange(e.target.value)} style={{ width: 220 }} />
          <button className="btn btn-sm btn-gold" disabled={saving || !name.trim()} onClick={onCreate}>{saving ? '⏳' : '+ '} {he ? 'צור' : 'Create'}</button>
          {activeScenarioId && (<>
            <button className="btn btn-sm btn-outline" onClick={onSaveName}>💾 {he ? 'שמור שם' : 'Save Name'}</button>
            <button className="btn btn-sm btn-outline" onClick={onApplyDefaults} title={he ? 'החל מחירי ברירת מחדל (Acton 3 = $135 וכו)' : 'Apply default cost rules (Acton 3 = $135 etc)'}>⚡ {he ? 'החל ברירות מחדל' : 'Apply Defaults'}</button>
            <button className="btn btn-sm btn-outline" onClick={onRefreshClone} title={he ? 'מסנכרן מ-cellcom העדכני' : 'Re-clone from current cellcom data'}>🔄 {he ? 'רענן clone' : 'Refresh Clone'}</button>
          </>)}
        </div>
        {scenarios.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {scenarios.map(s => (
              <div key={s.id} className={`badge`} style={{ padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: s.id === activeScenarioId ? 'var(--gold)' : '#eef2ff', color: s.id === activeScenarioId ? '#000' : '#1e293b', display: 'flex', gap: 6, alignItems: 'center' }}
                onClick={() => onLoad(s.id)}>
                <span>{s.name}</span>
                <span style={{ opacity: 0.6, fontSize: 11 }}>({s.order_count}o · {s.item_count}i)</span>
                <button className="btn btn-sm" style={{ padding: '0 4px', background: 'transparent', color: '#dc2626', border: 0, fontSize: 14 }} onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {!activeScenarioId ? (
        <div className="empty-state" style={{ padding: 32 }}>{he ? 'צור תרחיש חדש או בחר קיים מהרשימה' : 'Create a new scenario or pick one from the list'}</div>
      ) : (<>
        {/* Order picker */}
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>{he ? 'בחר הזמנות' : 'Select Orders'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
            {allOrders.map(o => {
              const onum = String(o.order_number);
              const isChecked = selected.has(onum);
              return (
                <label key={onum} style={{ display: 'flex', gap: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: isChecked ? '#f0fdf4' : '#fff', cursor: 'pointer', alignItems: 'center' }}>
                  <input type="checkbox" checked={isChecked} onChange={() => onToggleOrder(onum)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>#{onum} <span className={`badge ${o.status === 'completed' ? 'badge-success' : o.status === 'ready' ? 'badge-info' : 'badge-warn'}`} style={{ fontSize: 10 }}>{o.status}</span></div>
                    <div className="text-muted" style={{ fontSize: 11 }}>{o.item_count || 0} items · {o.total_units ? Number(o.total_units).toLocaleString() : 0} units · {fmtUsd(o.total_value)}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Items editor + totals */}
        {result && result.orders && result.orders.length > 0 && (() => {
          // Per-item shipping cost is computed by the backend (CBM × rate per origin).
          // Each item carries it.shippingPerUnit; for orders we sum to o.importShipping.
          // Build unique-SKU map for bulk editing across orders.
          const skuMap = {};
          for (const o of result.orders) {
            for (const it of (o.items || [])) {
              if (!skuMap[it.sku]) skuMap[it.sku] = { sku: it.sku, description: it.description, qty: 0, costs: new Set(), origin: it.origin, cbm: it.cbm };
              skuMap[it.sku].qty += it.qty;
              skuMap[it.sku].costs.add(it.simulatedCost);
            }
          }
          const uniqueSkus = Object.values(skuMap).sort((a, b) => String(a.description).localeCompare(String(b.description)));
          return (
          <div className="card" style={{ padding: 16 }}>
            {/* Bulk edit by SKU */}
            <details style={{ marginBottom: 16, background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: 12 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#5b21b6' }}>
                🔁 {he ? `עריכה גורפת לפי SKU (${uniqueSkus.length} ייחודיים)` : `Bulk Edit by SKU (${uniqueSkus.length} unique)`}
              </summary>
              <div className="text-muted" style={{ fontSize: 11, margin: '8px 0' }}>
                {he ? 'שינוי מחיר כאן יעדכן את כל הפריטים עם אותו SKU בכל ההזמנות הנבחרות.' : 'Change cost here → applies to ALL items with this SKU across all selected orders.'}
              </div>
              <div className="table-wrap"><table style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>SKU</th><th>Product</th><th className="text-end">Total Qty</th><th>Origin</th><th className="text-end">Current Cost</th><th className="text-end">Set All To</th>
                </tr></thead>
                <tbody>
                  {uniqueSkus.filter(s => !itemSearch || String(s.sku).toLowerCase().includes(itemSearch.toLowerCase()) || String(s.description || '').toLowerCase().includes(itemSearch.toLowerCase())).map(s => {
                    const costsArr = Array.from(s.costs);
                    const uniformCost = costsArr.length === 1 ? costsArr[0] : null;
                    return (
                      <tr key={s.sku}>
                        <td style={{ fontFamily: 'monospace' }}>{s.sku}</td>
                        <td>{s.description}</td>
                        <td className="text-end fw-bold">{s.qty}</td>
                        <td>{s.origin || '—'}</td>
                        <td className="text-end" style={{ color: uniformCost == null ? '#b45309' : '#374151' }}>
                          {uniformCost != null ? fmtUsd(uniformCost) : <span title={costsArr.join(', ')}>{he ? 'מעורב' : 'mixed'}: {costsArr.map(c => fmtUsd(c)).join(' / ')}</span>}
                        </td>
                        <td className="text-end" style={{ background: '#fef9c3' }}>
                          <input className="inline-edit" type="number" step="0.01" min="0" style={{ width: 90, textAlign: 'right', fontWeight: 700 }}
                            key={`bulk-${s.sku}-${uniformCost}`}
                            defaultValue={uniformCost != null ? uniformCost : ''}
                            placeholder="—"
                            onBlur={async e => {
                              const v = parseFloat(e.target.value);
                              if (!Number.isFinite(v) || v < 0) return;
                              if (uniformCost != null && v === uniformCost) return;
                              await onBulkCost(s.sku, v);
                            }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            </details>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, marginInlineEnd: 'auto' }}>{he ? 'פריטים — עריכת Simulated Cost' : 'Items — Edit Simulated Cost'}</h3>
              <input className="input input-sm" placeholder={he ? '🔍 חיפוש SKU / שם / SI...' : '🔍 Search SKU / name / SI...'}
                value={itemSearch} onChange={e => setItemSearch(e.target.value)} style={{ width: 260 }} />
              {itemSearch && (
                <button className="btn btn-sm btn-outline" onClick={() => setItemSearch('')} title={he ? 'נקה' : 'Clear'}>×</button>
              )}
            </div>
            {result.orders.map(o => (
              <div key={o.orderNumber} style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 8 }}>
                  Order #{o.orderNumber} <span className="text-muted" style={{ fontSize: 12, fontWeight: 400 }}>({o.status})</span>
                  <span style={{ marginInlineStart: 12, fontSize: 12, color: '#16a34a' }}>Shager Profit: <strong>{fmtUsd(o.shagerProfit)}</strong> ({o.shagerMargin}%)</span>
                </h4>
                <div className="table-wrap"><table style={{ fontSize: 12 }}><thead><tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('sku')}>SKU{arrow('sku')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('description')}>Product{arrow('description')}</th>
                  <th className="text-end" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('qty')}>Qty{arrow('qty')}</th>
                  <th className="text-end" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('sellingPrice')}>Sell Price{arrow('sellingPrice')}</th>
                  <th className="text-end" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('unitCost')}>Original Cost{arrow('unitCost')}</th>
                  <th className="text-end" style={{ background: '#fef9c3', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('simulatedCost')}>Simulated Cost{arrow('simulatedCost')}</th>
                  <th className="text-end" style={{ background: '#fce7f3', color: '#9d174d', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('landedCost')}>Landed Cost{arrow('landedCost')}</th>
                  <th className="text-end" style={{ background: '#fffbeb', color: '#a16207', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('mProfit')}>M Profit{arrow('mProfit')}</th>
                  <th className="text-end" style={{ background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('cProfit')}>C Profit{arrow('cProfit')}</th>
                  <th className="text-end" style={{ background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('shagerProfit')}>Shager Profit{arrow('shagerProfit')}</th>
                </tr></thead><tbody>
                  {sortItems(filterItems(o.items.map(it => ({ ...it, landedCost: Math.round(((it.simulatedCost || 0) + (it.shippingPerUnit || 0)) * 100) / 100 })))).map(it => (
                    <tr key={it.id}>
                      <td style={{ fontFamily: 'monospace' }}>{it.sku}</td>
                      <td>{it.description}</td>
                      <td className="text-end">{it.qty}</td>
                      <td className="text-end">{fmtUsd(it.sellingPrice)}</td>
                      <td className="text-end" style={{ color: '#6b7280' }}>{fmtUsd(it.unitCost)}</td>
                      <td className="text-end" style={{ background: '#fef9c3' }}>
                        <input className="inline-edit" type="number" step="0.01" min="0" style={{ width: 80, textAlign: 'right', fontWeight: 700 }}
                          key={`sc-${it.id}-${it.simulatedCost}`}
                          defaultValue={it.simulatedCost}
                          onBlur={e => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v !== it.simulatedCost) onCostChange(it.id, v); }} />
                      </td>
                      <td className="text-end" style={{ background: '#fce7f3', color: '#9d174d', fontWeight: 700 }}>{fmtUsd(it.landedCost)}</td>
                      <td className="text-end" style={{ background: '#fffbeb', color: '#a16207' }}>{fmtUsd(it.mProfit)}</td>
                      <td className="text-end" style={{ background: '#eff6ff', color: '#1d4ed8' }}>{fmtUsd(it.cProfit)}</td>
                      <td className="text-end" style={{ background: '#f0fdf4', color: it.shagerProfit >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{fmtUsd(it.shagerProfit)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700, background: '#f9fafb' }}>
                    <td colSpan={2}>Total</td>
                    <td className="text-end">{o.units}</td>
                    <td className="text-end">{fmtUsd(o.revenue)}</td>
                    <td className="text-end" style={{ color: '#6b7280' }}>{fmtUsd(o.originalCogs)}</td>
                    <td className="text-end" style={{ background: '#fef9c3' }}>{fmtUsd(o.simulatedCogs)}</td>
                    <td className="text-end" style={{ background: '#fce7f3', color: '#9d174d' }}>{fmtUsd(o.simulatedCogs + (o.importShipping || 0))}</td>
                    <td className="text-end" style={{ background: '#fffbeb', color: '#a16207' }}>{fmtUsd(o.mProfit)}</td>
                    <td className="text-end" style={{ background: '#eff6ff', color: '#1d4ed8' }}>{fmtUsd(o.cProfit)}</td>
                    <td className="text-end" style={{ background: '#f0fdf4', color: o.shagerProfit >= 0 ? '#16a34a' : '#dc2626' }}>{fmtUsd(o.shagerProfit)}</td>
                  </tr>
                </tbody></table></div>
              </div>
            ))}

            {/* Cellcom shipping expenses (M expenses per cellcom order) */}
            {result.orders && result.orders.some(o => o.mExp > 0) && (
              <div className="card" style={{ marginTop: 16, padding: 16, background: '#eff6ff', border: '1px solid #93c5fd' }}>
                <h4 style={{ margin: 0, marginBottom: 8, color: '#1e3a8a' }}>{he ? 'הוצאות שילוח לCellcom (m_expenses)' : 'Cellcom Shipping Expenses (m_expenses)'}</h4>
                <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
                  {he ? 'כבר משוקלל בחישוב הרווח פר-פריט (יורד מהכנסת שגר)' : 'Already factored into per-item profit (subtracted from Shager income)'}
                </div>
                <div className="table-wrap"><table style={{ fontSize: 12 }}>
                  <thead><tr>
                    <th>Order</th>
                    <th>Status</th>
                    <th className="text-end">M %</th>
                    <th className="text-end">Revenue</th>
                    <th className="text-end">M Commission</th>
                    <th className="text-end">M Expenses</th>
                  </tr></thead>
                  <tbody>
                    {result.orders.map(o => (
                      <tr key={o.id}>
                        <td style={{ fontFamily: 'monospace' }}>#{o.orderNumber}</td>
                        <td>{o.status}</td>
                        <td className="text-end">{(o.mPct * 100).toFixed(2)}%</td>
                        <td className="text-end">{fmtUsd(o.revenue)}</td>
                        <td className="text-end">{fmtUsd(o.mProfit)}</td>
                        <td className="text-end fw-bold" style={{ color: '#1e3a8a' }}>{fmtUsd(o.mExp)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #93c5fd', fontWeight: 700 }}>
                      <td colSpan={4}>Total</td>
                      <td className="text-end">{fmtUsd(result.totals.mProfit)}</td>
                      <td className="text-end" style={{ color: '#1e3a8a' }}>{fmtUsd(result.orders.reduce((s, o) => s + (o.mExp || 0), 0))}</td>
                    </tr>
                  </tbody>
                </table></div>
              </div>
            )}

            {/* Import shipping (CBM-based, per Origin) — Shager's import cost. */}
            {result.shipmentExpenses && result.shipmentExpenses.length > 0 && (
              <div className="card" style={{ marginTop: 16, padding: 16, background: '#fef2f2', border: '1px solid #fca5a5' }}>
                <h4 style={{ margin: 0, marginBottom: 8, color: '#991b1b' }}>{he ? 'הוצאות יבוא (CBM × Container rate)' : 'Import Shipping (CBM × Container rate)'}</h4>
                <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
                  {he ? 'לפי הגדרות Settings → Shipping Calculator. מופחת מרווח שגר לקבלת רווח נטו' : 'Per Settings → Shipping Calculator. Deducted from Shager profit to compute Net Profit'}
                </div>
                <div className="table-wrap"><table style={{ fontSize: 12 }}>
                  <thead><tr>
                    <th>Origin</th>
                    <th className="text-end">Units</th>
                    <th className="text-end">CBM</th>
                    <th className="text-end">$/CBM</th>
                    <th className="text-end">Total</th>
                  </tr></thead>
                  <tbody>
                    {result.shipmentExpenses.map(s => (
                      <tr key={s.origin}>
                        <td style={{ fontWeight: 700 }}>{s.origin}</td>
                        <td className="text-end">{s.units}</td>
                        <td className="text-end">{Number(s.cbm).toFixed(2)}</td>
                        <td className="text-end">{fmtUsd(s.ratePerCbm)}</td>
                        <td className="text-end fw-bold">{fmtUsd(s.total)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #fca5a5', fontWeight: 700 }}>
                      <td>Total</td>
                      <td colSpan={3}></td>
                      <td className="text-end" style={{ color: '#991b1b' }}>{fmtUsd(result.totals.importShipping)}</td>
                    </tr>
                  </tbody>
                </table></div>
              </div>
            )}

            {/* Grand totals */}
            <div style={{ marginTop: 16, padding: 16, background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', borderRadius: 10, border: '2px solid #16a34a' }}>
              <h3 style={{ margin: 0, marginBottom: 12 }}>{he ? 'סיכום כולל' : 'Grand Total'} — {result.orders.length} {he ? 'הזמנות' : 'orders'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                <Stat label={he ? 'יחידות' : 'Units'} value={result.totals.units} />
                <Stat label={'Revenue'} value={fmtUsd(result.totals.revenue)} />
                <Stat label={'Original COGS'} value={fmtUsd(result.totals.originalCogs)} color="#6b7280" />
                <Stat label={'Simulated COGS'} value={fmtUsd(result.totals.simulatedCogs)} color="#a16207" highlight />
                <Stat label={'Landed COGS'} value={fmtUsd(result.totals.simulatedCogs + result.totals.importShipping)} color="#9d174d" />
                <Stat label={'COGS Savings'} value={fmtUsd(result.totals.savings)} color={result.totals.savings >= 0 ? '#16a34a' : '#dc2626'} />
                <Stat label={'M Commission'} value={fmtUsd(result.totals.mProfit)} color="#a16207" />
                <Stat label={'M Expenses (cellcom)'} value={fmtUsd(result.orders.reduce((s, o) => s + (o.mExp || 0), 0))} color="#1e3a8a" />
                <Stat label={'C Profit'} value={fmtUsd(result.totals.cProfit)} color="#1d4ed8" />
                <Stat label={'Shager Profit (gross)'} value={fmtUsd(result.totals.shagerProfit)} color={result.totals.shagerProfit >= 0 ? '#16a34a' : '#dc2626'} />
                <Stat label={'– Import Shipping'} value={fmtUsd(result.totals.importShipping)} color="#991b1b" />
                <Stat label={'+ Q Support (2%)'} value={fmtUsd(result.totals.qSupport)} color="#0e7490" />
                <Stat label={'+ RMA (1.75%)'} value={fmtUsd(result.totals.rmaCredit)} color="#0e7490" />
                <Stat label={'– Customer Rebate (1%)'} value={fmtUsd(result.totals.customerRebate)} color="#991b1b" />
                <Stat label={'Shager Profit (net)'} value={fmtUsd(result.totals.shagerProfitNet)} color={result.totals.shagerProfitNet >= 0 ? '#16a34a' : '#dc2626'} highlight />
                <Stat label={'Net Margin'} value={result.totals.shagerMarginNet + '%'} color={result.totals.shagerMarginNet >= 0 ? '#16a34a' : '#dc2626'} />
                <Stat label={'Chain Profit (M+C+Shager)'} value={fmtUsd(result.totals.chainProfit)} color="#7c3aed" highlight />
                <Stat label={'Chain Margin'} value={result.totals.chainMargin + '%'} color="#7c3aed" />
              </div>
            </div>
          </div>
          );
        })()}
      </>)}
    </div>
  );
}

function Stat({ label, value, color, highlight }) {
  return (
    <div style={{ background: highlight ? '#fff' : 'transparent', padding: 10, borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || '#111' }}>{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// DEAL CHECK — April-2026-forward supplier SIs. Read-only: shows the true
// NET BUY PRICE PER UNIT (list − 1.75% − embedded credit) and whether each
// deal family is "according to deal" (net ≤ agreed × 0.9825). Does NOT change
// any profit total — this is a verification view of the embedded credits.
// ══════════════════════════════════════════════════════════════════════
function DealCheckView({ he }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try { const r = await api.get('/profitability/deal-check'); if (alive) setData(r.data); }
      catch { if (alive) setData(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="loading"><div className="spinner" />{he ? 'טוען…' : 'Loading…'}</div>;
  if (!data) return <div className="empty-state">{he ? 'שגיאה בטעינה' : 'Failed to load'}</div>;

  const sum = data.summary || {};
  const allGood = (sum.offDealLines || 0) === 0;
  const money = (n) => fmt(n);

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-dim)' }}>
        {he
          ? 'מאפריל 2026 הזיכוי מוטמע בחשבונית הספק. כאן מוצג מחיר הקנייה נטו ליחידה (מחירון − 1.75% − זיכוי) ובדיקה מול העסקה. תצוגה בלבד — לא משנה רווחיות.'
          : 'Since April 2026 the credit is embedded in the supplier invoice. This shows the net buy price per unit (list − 1.75% − credit) vs the deal. View only — does not change profit.'}
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: 12, marginBottom: 16,
        background: allGood ? '#dcfce7' : '#fef2f2', border: `1px solid ${allGood ? '#86efac' : '#fca5a5'}`,
        borderRadius: 8, color: allGood ? '#166534' : '#991b1b', fontWeight: 700 }}>
        <span style={{ fontSize: 18 }}>{allGood ? '✓' : '⚠'}</span>
        <span>{allGood
          ? (he ? 'כל השורות לפי העסקה' : 'All lines according to deal')
          : (he ? `${sum.offDealLines} שורות בחריגה` : `${sum.offDealLines} lines off deal`)}</span>
        <span style={{ fontWeight: 400 }}>
          {he
            ? `${sum.invoiceCount} חשבוניות · ${sum.linesChecked} שורות נבדקו`
            : `${sum.invoiceCount} invoices · ${sum.linesChecked} lines checked`}
        </span>
      </div>

      {(data.invoices || []).map(inv => (
        <div key={inv.orderNo} style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', background: 'var(--bg-subtle, #f9fafb)', borderBottom: '1px solid var(--border)' }}>
            <strong>{inv.orderNo}</strong>
            {inv.externalDocNo && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{inv.externalDocNo}</span>}
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{inv.orderDate}</span>
            <span style={{ marginInlineStart: 'auto', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              background: inv.allOnDeal ? '#dcfce7' : '#fef2f2', color: inv.allOnDeal ? '#166534' : '#991b1b' }}>
              {inv.allOnDeal ? (he ? '✓ לפי העסקה' : '✓ On deal') : (he ? `⚠ ${inv.offDealCount} חריגות` : `⚠ ${inv.offDealCount} off`)}
            </span>
          </div>
          <table className="data-table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'start' }}>{he ? 'מוצר' : 'Product'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'יח\'' : 'Units'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'מחירון' : 'List'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'זיכוי ליח\'' : 'Credit/u'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'נטו ליח\'' : 'Net/unit'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'מחיר עסקה' : 'Deal price'}</th>
                <th style={{ textAlign: 'end' }}>{he ? 'פער' : 'Δ'}</th>
                <th style={{ textAlign: 'center' }}>{he ? 'סטטוס' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'start' }}>{l.description}</td>
                  <td style={{ textAlign: 'end' }}>{fmtInt(l.units)}</td>
                  <td style={{ textAlign: 'end' }}>{money(l.listPrice)}</td>
                  <td style={{ textAlign: 'end' }}>{l.units > 0 ? money(l.creditAllocated / l.units) : '—'}</td>
                  <td style={{ textAlign: 'end', fontWeight: 700 }}>{money(l.netUnit)}</td>
                  <td style={{ textAlign: 'end', color: 'var(--text-dim)' }}>{money(l.dealPrice)}</td>
                  <td style={{ textAlign: 'end', color: l.deltaPerUnit > 0.005 ? '#991b1b' : '#166534' }}>
                    {(l.deltaPerUnit > 0 ? '+' : '') + l.deltaPerUnit.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {l.onDeal
                      ? <span style={{ color: '#166534', fontWeight: 700 }}>✓</span>
                      : <span style={{ color: '#991b1b', fontWeight: 700 }}>{he ? 'חריגה' : 'off'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {inv.crossCreditKept > 0 && (
            <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--text-dim)' }}>
              {he ? 'זיכוי חוצה-תקופה (לא מהתקופה הזו): ' : 'Cross-period credit (not this period): '}{money(inv.crossCreditKept)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
